'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getImageUrl } from '@/lib/image';
import { PermissionGate } from '@/features/auth';
import type { Client } from '../types';

interface ClientCompactRowProps {
  client: Client;
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-400',
  inactive: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400'
};

export function ClientCompactRow({
  client,
  onEdit,
  onDelete
}: ClientCompactRowProps) {
  const t = useTranslations('clients');

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <Avatar className='h-8 w-8 shrink-0 rounded-md'>
        {client.logo_url && (
          <AvatarImage
            src={getImageUrl(client.logo_url)}
            alt={client.name}
            className='object-cover'
          />
        )}
        <AvatarFallback className='rounded-md text-xs font-bold'>
          {client.name.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-medium'>{client.name}</span>
          <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
            {client.client_id}
          </span>
        </div>
      </div>

      <span className='text-muted-foreground hidden text-sm md:inline'>
        {client.admin_email}
      </span>

      <Badge
        variant='outline'
        className={cn(
          'shrink-0 text-xs uppercase',
          STATUS_STYLES[client.status]
        )}
      >
        {client.status === 'active' ? t('statusActive') : t('statusInactive')}
      </Badge>

      <div className='flex shrink-0 items-center gap-1'>
        <PermissionGate permissions={['clients.edit']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onEdit(client)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['clients.delete']}>
          <Button
            variant='ghost'
            size='icon'
            className='text-destructive h-7 w-7'
            onClick={() => onDelete(client)}
          >
            <Trash2 className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
